<script setup lang="ts">
import { computed, h, provide, onMounted } from 'vue'
import { RouterView, routerKey, routerViewLocationKey } from 'vue-router'
import {
  NConfigProvider, NLayout, NLayoutHeader, NLayoutContent, NLayoutFooter,
  NMenu, NCard, NGradientText, NMessageProvider, NText
} from 'naive-ui'
import { lightTheme } from 'naive-ui'
import router from './router'
import './assets/main.css'
import 'uno.css'

// 注入内部路由，供 RouterView 使用（主应用使用独立的 history 路由分发，本子模块使用独立 hash 路由）
provide(routerKey, router)
// 提供根级路由上下文，供顶层 RouterView 读取当前路由（否则 inject(routerViewLocationKey) 为 undefined 导致崩溃）
provide(routerViewLocationKey, router.currentRoute)

const activeKey = computed(() => router.currentRoute.value.path)

// 导航菜单点击处理
function handleMenuUpdate(key: string) {
  router.push(key)
}

// 返回主站首页（通过主应用 history 路由分发，回到 /）
function goHome() {
  history.pushState({}, '', '/')
  window.dispatchEvent(new PopStateEvent('popstate'))
}

// 由于子模块未通过 app.use(router) 安装，需手动触发首次路由解析并建立 history 监听，
// 否则 currentRoute 停留在 START_LOCATION（matched 为空），RouterView 默认不渲染任何内容
onMounted(() => {
  ;(router.options.history as unknown as { setupListeners?: () => void }).setupListeners?.()
  const hash = window.location.hash
  const path = hash && hash.length > 1 ? hash.slice(1) : '/'
  router.push(path).catch(() => {})
})

// 主题覆盖配置
const themeOverrides = {
  common: {
    bodyColor: '#f5f5f5',
    primaryColor: '#8b5cf6',
    primaryColorHover: '#7c3aed',
    primaryColorPressed: '#6d28d9',
    primaryColorSuppl: '#a78bfa'
  }
}

// 导航菜单配置
const menuOptions = [
  { label: '圆角处理', key: '/', icon: () => h('div', { class: 'i-carbon-image' }) },
  { label: '比例裁剪', key: '/crop', icon: () => h('div', { class: 'i-carbon-crop' }) },
  { label: '等高裁剪', key: '/height-crop', icon: () => h('div', { class: 'i-carbon-split-screen' }) },
  { label: '格式转换', key: '/convert', icon: () => h('div', { class: 'i-carbon-repeat' }) },
  { label: '图片压缩', key: '/compress', icon: () => h('div', { class: 'i-carbon-compress' }) },
  { label: 'Favicon生成', key: '/favicon', icon: () => h('div', { class: 'i-carbon-favorite' }) }
]
</script>

<template>
  <n-config-provider :theme="lightTheme" :theme-overrides="themeOverrides">
    <n-message-provider>
      <n-layout class="layout-container">
        <!-- 顶部导航栏 -->
        <n-layout-header bordered class="header-shadow">
          <div class="header-content">
            <button class="home-btn" title="返回首页" @click="goHome">
              <div class="i-carbon-home"></div>
            </button>
            <div class="logo-container">
              <n-gradient-text type="primary" size="24">图片处理工具</n-gradient-text>
            </div>
            <div class="nav-menu">
              <n-menu :value="activeKey" mode="horizontal" :options="menuOptions" @update:value="handleMenuUpdate" />
            </div>
          </div>
        </n-layout-header>

        <!-- 主内容区域 -->
        <n-layout-content class="content-container">
          <n-card class="content-card">
            <RouterView />
          </n-card>
        </n-layout-content>

        <!-- 页脚 -->
        <n-layout-footer bordered>
          <div class="footer-content">
            <n-text depth="3">© 2026 魔法代码</n-text>
          </div>
        </n-layout-footer>
      </n-layout>
    </n-message-provider>
  </n-config-provider>
</template>

<style scoped>
.layout-container {
  background-color: var(--color-background);
}

.header-shadow {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  height: 64px;
}

.logo-container {
  flex-shrink: 0;
}

.home-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  background-color: rgba(139, 92, 246, 0.1);
  color: #8b5cf6;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.home-btn:hover {
  background-color: rgba(139, 92, 246, 0.18);
  transform: translateY(-1px);
}

.home-btn:active {
  transform: translateY(0);
}

.home-btn div {
  width: 20px;
  height: 20px;
}

.nav-menu {
  flex: 1;
  display: flex;
  justify-content: center;
}

.content-container {
  padding: 32px;
  min-height: calc(100vh - 128px);
  width: 100%;
}

.content-card {
  max-width: 1600px;
  margin: 0 auto;
  padding: 24px;
}

.footer-content {
  text-align: center;
  padding: 16px 0;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    height: auto;
    padding: 12px;
  }

  .nav-menu {
    margin: 12px 0;
  }

  .content-container {
    padding: 16px;
  }
}
</style>
